import React, { Component, ErrorInfo, ReactNode } from "react";
import { Sentry } from "@/lib/sentry";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  /** Friendly name shown in the fallback UI, e.g. "Dashboard" */
  featureName?: string;
}

interface State {
  hasError: boolean;
}

/**
 * A feature-level error boundary that shows a contained fallback
 * instead of crashing the entire app.
 */
export class FeatureErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false };

  public static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack },
        feature: { name: this.props.featureName ?? "unknown" },
      },
    });
  }

  private handleRetry = () => {
    this.setState({ hasError: false });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center min-h-[200px]">
          <AlertTriangle className="w-10 h-10 text-destructive mb-3" />
          <h3 className="font-display font-bold text-lg mb-1">
            Something went wrong
          </h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-sm">
            {this.props.featureName
              ? `The ${this.props.featureName} section encountered an error.`
              : "This section encountered an error."}{" "}
            The rest of the app still works.
          </p>
          <Button variant="outline" size="sm" onClick={this.handleRetry}>
            Try again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default FeatureErrorBoundary;
